import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';

import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  radii,
} from '../../../theme/tokens';
import type { EntityPageClassifiedLink } from './entityPageLinkGroups';
import { EntityPanelFrame } from './EntityPanelFrame';
import { useEntityPageModel } from './useEntityPageModel';

const isExternalLink = (value: string) => /^https?:\/\//.test(value);

const getLinkIcon = (link: EntityPageClassifiedLink) => {
  switch (link.classification) {
    case 'dashboard':
      return <QueryStatsOutlinedIcon fontSize="small" />;
    case 'logs':
      return <StorageOutlinedIcon fontSize="small" />;
    case 'pagerduty':
    case 'slo':
      return <PolicyOutlinedIcon fontSize="small" />;
    case 'source':
      return <DashboardOutlinedIcon fontSize="small" />;
    default:
      return <LinkOutlinedIcon fontSize="small" />;
  }
};

const UsefulLinkRow = ({ link }: { link: EntityPageClassifiedLink }) => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const external = isExternalLink(link.url);

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'grid',
        gap: 1.25,
        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: getSurfaceColor(theme, 'subtle'),
          border: `1px solid ${getSurfaceBorder(theme)}`,
          borderRadius: `${radii.md}px`,
          color: theme.palette.primary.main,
          display: 'inline-flex',
          height: 34,
          justifyContent: 'center',
          width: 34,
        }}
      >
        {getLinkIcon(link)}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: 15,
            fontWeight: 700,
            lineHeight: 1.3,
          }}
        >
          {link.title}
        </Typography>
        <Typography
          sx={{
            color: tokens.textMuted,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {link.classification === 'dashboard'
            ? 'Metrics and visualizations'
            : link.classification === 'logs'
              ? 'Logs and operational traces'
              : link.classification === 'pagerduty'
                ? 'Incident response and ownership'
                : link.classification === 'source'
                  ? 'Source repository'
                  : 'Supporting service link'}
        </Typography>
      </Box>

      <Link
        component={external ? 'a' : RouterLink}
        href={external ? link.url : undefined}
        rel={external ? 'noreferrer' : undefined}
        sx={{
          alignItems: 'center',
          color: theme.palette.primary.main,
          display: 'inline-flex',
          fontSize: 14,
          fontWeight: 700,
          gap: 0.625,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
        }}
        target={external ? '_blank' : undefined}
        to={external ? undefined : link.url}
        underline="none"
      >
        Open
        <OpenInNewOutlinedIcon fontSize="inherit" />
      </Link>
    </Box>
  );
};

export const EntityUsefulLinksPanel = () => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const model = useEntityPageModel();

  return (
    <EntityPanelFrame title="Useful links">
      {model.links.useful.length ? (
        <Stack spacing={1.75}>
          {model.links.useful.slice(0, 5).map(link => (
            <UsefulLinkRow key={`${link.title}:${link.url}`} link={link} />
          ))}
        </Stack>
      ) : (
        <Typography
          sx={{
            color: tokens.textMuted,
            fontSize: 14,
            lineHeight: 1.7,
          }}
        >
          No operational or supporting links are configured for this entity yet.
        </Typography>
      )}
    </EntityPanelFrame>
  );
};
